import { useCallback, useMemo } from "react";
import { useNavigate } from "@remix-run/react";
import { useHref } from "@made-people/grease-link";
import { useTranslate } from "@made-people/grease-translations";
import { getAttributeBooleanValue, getAttributeValue } from "../helpers";
import type { Product, ProductBase } from "@made-people/grease-product";

type FeatureToggleProps = {
  productBase: ProductBase;
  relatedProduct: Product;
  featureOptions: [string, string];
  productProperty: string;
  activeOptionProperty: string;
  title: string;
};

const getDisplayValue = (
  base: ProductBase,
  productProperty: string,
  translate: (key: string) => string,
) => {
  if (productProperty === "shaping") {
    const shapingValue = getAttributeBooleanValue(base, "isLightShaping");
    return shapingValue
      ? translate("Toggle_Shaping_Option_Light_Shaping")
      : translate("Toggle_Shaping_Option_No_Shaping");
  }
  if (productProperty === "sidewing") {
    const sideWingValue = getAttributeBooleanValue(base, "hasSideWing");
    return sideWingValue
      ? translate("Toggle_SideWing_Option_Yes_Side_Wing")
      : translate("Toggle_SideWing_Option_No_Side_Wing");
  }
  if (productProperty === "material_aspect") {
    const laceValue = getAttributeBooleanValue(base, "hasLace");
    return laceValue
      ? String(getAttributeValue(base, productProperty) ?? "")
      : translate("Related_Lace_Product_Material_Plain");
  }
  return String(getAttributeValue(base, productProperty) ?? "");
};

export const FeatureToggle = (props: FeatureToggleProps) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const href = useHref();
  const optionProducts = useMemo(
    () =>
      [props.productBase, props.relatedProduct.base].sort((a, b) =>
        a.productId.localeCompare(b.productId),
      ),
    [props.productBase, props.relatedProduct.base],
  );
  const currentProductId = props.productBase.productId;

  const handleClick = useCallback(
    (targetSlug: string, active: boolean) => {
      if (active) {
        return;
      }
      const targetUrl = href(targetSlug);
      if (!targetUrl) {
        return;
      }
      navigate(targetUrl);
    },
    [href, navigate],
  );

  return (
    <div className="my-5 flex flex-col gap-3">
      <span className="text-theme-label-sm">{translate(props.title)}</span>
      <div className="rounded-md bg-[#E2DFD7] p-2 w-full lg:w-fit">
        <div className="grid grid-cols-2 gap-[10px] w-full lg:w-fit">
          {optionProducts.map((optionProductBase) => {
            const active = optionProductBase.productId === currentProductId;
            const displayValue = getDisplayValue(
              optionProductBase,
              props.productProperty,
              translate,
            );
            return (
              <button
                key={optionProductBase.productId}
                type="button"
                disabled={active}
                onClick={() => handleClick(optionProductBase.slug, active)}
                className={`min-h-10 px-4 flex justify-center gap-2 transition-all items-center text-center text-sm leading-snug tracking-[1px] w-full lg:w-[200px] rounded-md  ${
                  active
                    ? "gap-2 bg-[#383838] text-white cursor-not-allowed"
                    : "cursor-pointer hover:bg-[#38383833]"
                }`}
              >
                <span>{displayValue}</span>
                {active && <span aria-hidden="true">✓</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
